import { Box, Container, Flex, Paper, Text, Button, Badge, Tabs, Loader, Center, Title, Divider, Stack } from "@mantine/core";
import {
  IconBuilding,
  IconPhone,
  IconMail,
  IconLock,
  IconArrowLeft,
  IconBookmark,
  IconRosetteDiscountCheckFilled,
  IconExclamationCircle,
  IconCheck,
  IconX,
  IconDownload,
} from "@tabler/icons-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useDisclosure } from "@mantine/hooks";
import { successNotification } from "@/shared/utils/successNotification";
import ProfileTab from "./ProfileTab";
import DocumentsTab from "./DocumentsTab";
import ImagesTabContent from "@/components/profile/ImagesTabContent";
import { useNavigate, useParams } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util.ts";
import { useAuth } from "@/hooks/useAuth";
import ShortlistModal from "@/components/shared/UserSignupModal";
import GenericModal from "@/components/shared/GenericModal";
import ExpressInterestModal, { ExpressInterestFormData } from "@/components/shared/ExpressInterestModal";
import { useUser } from "@/context/UserContext";
import InfoBanner from "@/components/shared/InfoBanner";
import { STATUS_COLORS, CollaborationStatus } from "@/components/shared/constants/general.const.ts";
import { PageID, PAGE_CONFIG } from "./AnonymousProfile.config";
import ProfileVerificationStepper from "@/pages/profile-verification/ProfileVerificationStepper";
import { getCollaborationStatusConfig } from "@/shared/utils/collaboration-helpers";
import AcceptInterestModal from "@/pages/interests/components/AcceptInterestModal";
import { downloadAnonymizedProfilePdf } from "@/utils/anonymizedProfilePdf";
import { errorNotification } from "@/shared/utils/errorNotification";
import PaymentWindowExtendModal from "@/components/shared/PaymentWindowExtendModal";
import {
  formatPaymentHoursLabel,
  isPaymentUiWarningUrgent,
  usePaymentWindowUiDemo,
} from "@/shared/utils/paymentWindowUiDemo";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TAB_LABEL_FZ,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  navyProfileNavTabBorderRadius,
} from "@/shared/styles/navTabsLayout";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import { useNavTabsStackLayout } from "@/shared/styles/useNavTabsStackLayout";

/** Mobile anonymized header: fixed gutter so GTID title / badge align with contact-line text (after icons). */
const ANON_MOBILE_DETAIL_ICON_COL_PX = 22;

const BOOKMARK_HEADER_BTN_STYLES = {
  root: {
    backgroundColor: "#04305c",
    color: "white",
    border: "1px solid #04305c",
    "&:hover": {
      backgroundColor: "#032847",
      borderColor: "#032847",
    },
  },
};

interface AnonymousProfileProps {
  pageID: PageID;
  companyData?: any;
  onDownloadVerificationPdf?: () => void | Promise<void>;
  verificationPdfDownloading?: boolean;
}

export default function AnonymousProfile({
  pageID,
  companyData,
  onDownloadVerificationPdf,
  verificationPdfDownloading,
}: AnonymousProfileProps) {
  const config = PAGE_CONFIG[pageID];

  const { backLink, backText, containerSize, hideBackButton, hideInfoBanner, hideInterestCTA, disableShortlist } =
    config;

  const navigate = useNavigate();
  const { id } = useParams();
  const [isFavorited, setIsFavorited] = useState(false);
  const [company, setCompany] = useState<any>(companyData || null);
  const [loading, setLoading] = useState(!companyData);
  const [collaborationStatus, setCollaborationStatus] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string | null>("profile");
  const {
    isMobile: isProfileNavMobile,
    tabsListStyles: profileNavTabsListStyles,
    tabsRootStyles: profileNavTabsRootStyles,
  } = useNavTabsStackLayout(2);

  const { isAuthenticated } = useAuth();
  const [modalOpened, setModalOpened] = useState(false);
  const [expressInterestModalOpened, { open: openExpressInterestModal, close: closeExpressInterestModal }] =
    useDisclosure(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [acceptModalOpened, setAcceptModalOpened] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [removeModalOpened, { open: openRemoveModal, close: closeRemoveModal }] = useDisclosure(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [anonymizedPdfLoading, setAnonymizedPdfLoading] = useState(false);

  const {
    isLoggedIn,
    userDetails,
    shortlistedIds,
    addToShortlist,
    removeFromShortlist,
    hasCollaborationWith,
    addInterestedCompany,
    removeInterestedCompany,
    refreshPendingInterestCount,
  } = useUser();

  const refreshCompany = useCallback(async () => {
    // If companyData is provided, use it directly
    if (companyData) {
      setCompany(companyData);
      setLoading(false);
      return;
    }

    if (!id) {
      navigate("/");
      return;
    }

    setLoading(true);

    try {
      const response = await trpc.company.publicGetById.query({ companyId: id });
      setCompany(response);
    } catch (err) {
      console.error("Error fetching company:", err);
      // For public routes, don't call handleTrpcError to avoid login redirects
      if (pageID === "DISCOVER_PUBLIC") {
        navigate("/");
      } else {
        // For authenticated routes, use error handler
        handleTrpcError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [companyData, id, navigate, pageID]);

  // Check shortlist status from context
  useEffect(() => {
    if (disableShortlist) return;
    const companyIdToCheck = companyData?.id || id;
    if (companyIdToCheck) {
      setIsFavorited(shortlistedIds.has(companyIdToCheck));
    }
  }, [shortlistedIds, id, companyData, disableShortlist]);

  // Fetch collaboration status only if company ID exists in interestedCompanyIds
  useEffect(() => {
    if (!isAuthenticated || disableShortlist) return;
    const companyIdToCheck = companyData?.id || id;
    if (!companyIdToCheck) return;

    // Only fetch if we know there's a collaboration
    if (hasCollaborationWith(companyIdToCheck)) {
      trpc.collaboration.getByCompanyId
        .query({ targetCompanyId: companyIdToCheck })
        .then((collaboration) => {
          setCollaborationStatus(collaboration);
        })
        .catch((err) => {
          console.error("Error fetching collaboration:", err);
          setCollaborationStatus(null);
        });
    } else {
      setCollaborationStatus(null);
    }
  }, [isAuthenticated, id, companyData, hasCollaborationWith, disableShortlist]);

  const bothSignedNda = collaborationStatus?.requesterSignedNDA && collaborationStatus?.receiverSignedNDA;

  const isPaymentPhase =
    Boolean(collaborationStatus?.paymentDeadline) &&
    (collaborationStatus?.status === CollaborationStatus.BOTH_ACCEPTED ||
      collaborationStatus?.status === CollaborationStatus.REQUESTER_PAID_ONLY ||
      collaborationStatus?.status === CollaborationStatus.RECEIVER_PAID_ONLY);

  const { displayMs, modalOpen, handleExtend, handleModalDismiss } = usePaymentWindowUiDemo(
    collaborationStatus?.id,
    collaborationStatus?.paymentDeadline,
    isPaymentPhase,
  );

  const userCompanyId = userDetails?.company?.id;
  const canIncomingReceiverDownloadAnonymizedPdf =
    Boolean(
      company &&
      isLoggedIn &&
      collaborationStatus?.status === CollaborationStatus.REQUESTED &&
      userCompanyId === collaborationStatus.receiverCompanyId &&
      company.id === collaborationStatus.requesterCompanyId
    );

  const handleDownloadAnonymizedProfilePdf = useCallback(async () => {
    if (!company) return;
    setAnonymizedPdfLoading(true);
    try {
      await downloadAnonymizedProfilePdf(company, collaborationStatus);
    } catch (err) {
      errorNotification({
        title: "Could not create PDF",
        message: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setAnonymizedPdfLoading(false);
    }
  }, [company, collaborationStatus]);

  // Initial fetch effect
  useEffect(() => {
    refreshCompany();
  }, [refreshCompany]);

  const handleBackClick = () => {
    if (backLink) {
      navigate(backLink);
    } else if (isLoggedIn) {
      navigate("/my-discovery");
    } else {
      navigate("/");
    }
  };


  const handleShortlistClick = async () => {
    if (!isAuthenticated) {
      setModalOpened(true);
      return;
    }

    const companyIdToUse = companyData?.id || id;
    if (!companyIdToUse) return;

    if (!isFavorited) {
      // Add to shortlist
      try {
        await addToShortlist(companyIdToUse);
        successNotification({
          title: "Success",
          message: "Company added to shortlist",
        });
      } catch (err) {
        handleTrpcError(err);
      }
    } else {
      openRemoveModal();
    }
  };

  const handleRemoveConfirm = async () => {
    const companyIdToUse = companyData?.id || id;
    if (!companyIdToUse) return;

    setRemoveLoading(true);
    try {
      await removeFromShortlist(companyIdToUse);
      successNotification({
        title: "Success",
        message: "Company removed from shortlist",
      });
      closeRemoveModal();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleSignUpClick = () => {
    navigate("/signup");
  };

  const handleBrowseCompaniesClick = () => {
    navigate("/");
  };

  const handleExpressInterestClick = () => {
    openExpressInterestModal();
  };

  const handleConfirmExpressInterest = async (data: ExpressInterestFormData) => {
    const companyIdToUse = companyData?.id || id;
    if (!companyIdToUse) return;

    setRequestLoading(true);
    try {
      await trpc.collaboration.request.mutate({
        targetCompanyId: companyIdToUse,
        requesterRole: data.requesterRole,
        expectedPartnerRole: data.expectedPartnerRole,
      });
      refreshPendingInterestCount();
      successNotification({
        title: "Success",
        message: "Your interest has been expressed to the company",
      });
      closeExpressInterestModal();

      // Add to interested companies and fetch fresh collaboration
      addInterestedCompany(companyIdToUse);
      const collaboration = await trpc.collaboration.getByCompanyId.query({ targetCompanyId: companyIdToUse });
      setCollaborationStatus(collaboration);
    } catch (error) {
      handleTrpcError(error);
      closeExpressInterestModal();
    } finally {
      setRequestLoading(false);
    }
  };

  const handleAcceptConfirm = async () => {
    if (!collaborationStatus?.id) return;

    setAcceptLoading(true);
    try {
      await trpc.collaboration.accept.mutate({ matchId: collaborationStatus.id });
      successNotification({
        title: "Success",
        message: "Collaboration request accepted!",
      });
      setAcceptModalOpened(false);

      const companyIdToUse = companyData?.id || id;
      if (companyIdToUse) {
        // Fetch fresh collaboration data
        const collaboration = await trpc.collaboration.getByCompanyId.query({ targetCompanyId: companyIdToUse });
        setCollaborationStatus(collaboration);
      }
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!collaborationStatus?.id) return;

    setDeclineLoading(true);
    try {
      await trpc.collaboration.reject.mutate({ matchId: collaborationStatus.id });
      successNotification({ title: "Success", message: "Collaboration request declined." });

      const companyIdToUse = companyData?.id || id;
      if (companyIdToUse) {
        // Remove from interested companies and clear collaboration status
        removeInterestedCompany(companyIdToUse);
        setCollaborationStatus(null);
      }
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setDeclineLoading(false);
    }
  };

  const headquartersFlagEmoji = useMemo(
    () => flagEmojiFromHeadquartersCountry(company?.headquartersCountry),
    [company?.headquartersCountry]
  );

  if (loading) {
    return (
      <Center py="xl" mih="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!company) {
    return (
      <Container size={containerSize} py="xl">
        <Text>Company not found</Text>
      </Container>
    );
  }

  const showProfileVerificationStepper =
    pageID === "MY_PROFILE_PREVIEW" &&
    ["DRAFT", "UNDER_REVIEW", "VERIFIED", "SYSTEM_VERIFIED", "ACTION_REQUIRED"].includes(company.status);

  return (
    <Box py="sm">
      {/* Mobile: Back Link */}
      {!hideBackButton && (
        <Box py="sm" hiddenFrom="sm">
          <Container size={containerSize}>
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconArrowLeft size={16} />}
              size="sm"
              p={0}
              onClick={handleBackClick}
            >
              {backText || "Back to Discovery"}
            </Button>
          </Container>
        </Box>
      )}

      {/* Alert Banner */}
      {!hideInfoBanner && !isLoggedIn && (
        <Box bg={STATUS_COLORS.WARNING.bg} w="100%">
          <Container size={containerSize}>
            <InfoBanner
              icon={IconLock}
              iconSize={18}
              iconColor={STATUS_COLORS.WARNING.icon}
              title="Sign up to unlock full company details and express interest"
              subtitle="View contact information, download documents, and connect with this company"
              bgColor="transparent"
              titleColor={STATUS_COLORS.WARNING.text}
              subtitleColor={STATUS_COLORS.WARNING.text}
              borderColor="transparent"
            />
          </Container>
        </Box>
      )}

      {/* Desktop: Header Navigation */}
      {!hideBackButton && (
        <Box py="sm" visibleFrom="sm">
          <Container size={containerSize}>
            <Flex justify="space-between" align="center">
              <Button
                variant="subtle"
                color="gray"
                leftSection={<IconArrowLeft size={16} />}
                size="sm"
                onClick={handleBackClick}
              >
                {backText || "Back to Discovery"}
              </Button>
            </Flex>
          </Container>
        </Box>
      )}

      {/* Company Header */}
      <Container size={containerSize}>
        {/* Mobile: Company Card */}
        <Paper
          p="md"
          radius="lg"
          mb="md"
          style={{
            border: "1px solid #04305c",
            backgroundColor: "#ebf0ff",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
            maxWidth: "100%",
            overflow: "hidden",
          }}
          hiddenFrom="sm"
        >
          <Stack gap="md" w="100%" maw="100%" style={{ minWidth: 0 }}>
            {/* One row: company data (logo + details) | verification + shortlist */}
            <Flex gap="sm" align="flex-start" w="100%" maw="100%" wrap="nowrap" miw={0}>
              <Flex gap="sm" align="flex-start" style={{ flex: 1, minWidth: 0 }} maw="100%" miw={0}>
                <Flex
                  w={48}
                  h={48}
                  style={{
                    borderRadius: 8,
                    border: "1px solid #04305c",
                    backgroundColor: "#F9FAFB",
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                  align="center"
                  justify="center"
                >
                  <IconBuilding size={24} color="#6B7280" />
                </Flex>

                <Flex direction="column" style={{ flex: 1, minWidth: 0 }} miw={0}>
                  <Flex align="flex-start" gap="xs" mb="xs" wrap="nowrap" miw={0}>
                    <Box
                      miw={ANON_MOBILE_DETAIL_ICON_COL_PX}
                      w={ANON_MOBILE_DETAIL_ICON_COL_PX}
                      style={{ flexShrink: 0 }}
                    />
                    <Flex align="center" gap="xs" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
                      <Text size="md" fw={400} style={{ wordBreak: "break-word" }}>
                        {company.gtid}
                      </Text>
                      {company.status === "VERIFIED" && (
                        <IconRosetteDiscountCheckFilled size={20} color="var(--mantine-color-primary-6)" />
                      )}
                    </Flex>
                  </Flex>

                  {company.gtid && (
                    <Flex align="flex-start" gap="xs" mb="xs" wrap="nowrap" miw={0}>
                      <Box
                        miw={ANON_MOBILE_DETAIL_ICON_COL_PX}
                        w={ANON_MOBILE_DETAIL_ICON_COL_PX}
                        style={{ flexShrink: 0 }}
                      />
                      <Badge
                        variant="light"
                        color="#EEF2F7"
                        size="sm"
                        radius="sm"
                        style={{
                          width: "fit-content",
                          maxWidth: "100%",
                          backgroundColor: "#EEF2F7",
                          color: "#374151",
                        }}
                      >
                        {company.gtid}
                      </Badge>
                    </Flex>
                  )}

                  <Flex direction="column" gap={4}>
                    <Flex align="flex-start" gap="xs" wrap="nowrap" miw={0}>
                      <Box
                        miw={ANON_MOBILE_DETAIL_ICON_COL_PX}
                        w={ANON_MOBILE_DETAIL_ICON_COL_PX}
                        style={{ flexShrink: 0 }}
                      />
                      {headquartersFlagEmoji ? (
                        <Text
                          component="span"
                          fz={16}
                          lh={1}
                          pt={2}
                          role="img"
                          aria-label={`${company.headquartersCountry ?? "Country"} flag`}
                          style={{ flexShrink: 0 }}
                        >
                          {headquartersFlagEmoji}
                        </Text>
                      ) : null}
                      <Text size="xs" c="slate.7" style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                        {company.headquartersCountry || "London, United Kingdom"}
                      </Text>
                    </Flex>
                    <Flex align="flex-start" gap="xs" wrap="nowrap" miw={0}>
                      <Flex
                        miw={ANON_MOBILE_DETAIL_ICON_COL_PX}
                        w={ANON_MOBILE_DETAIL_ICON_COL_PX}
                        justify="center"
                        align="flex-start"
                        pt={2}
                        style={{ flexShrink: 0 }}
                      >
                        <IconPhone size={14} color="#6B7280" />
                      </Flex>
                      <Text size="xs" c="slate.7" style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                        {company.phone || "XXXX XXXXX"}
                      </Text>
                    </Flex>
                    <Flex align="flex-start" gap="xs" wrap="nowrap" miw={0}>
                      <Flex
                        miw={ANON_MOBILE_DETAIL_ICON_COL_PX}
                        w={ANON_MOBILE_DETAIL_ICON_COL_PX}
                        justify="center"
                        align="flex-start"
                        pt={2}
                        style={{ flexShrink: 0 }}
                      >
                        <IconMail size={14} color="#6B7280" />
                      </Flex>
                      <Text size="xs" c="slate.7" style={{ flex: 1, minWidth: 0, wordBreak: "break-all" }}>
                        {"XXXXX@mail.com"}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>

              {(showProfileVerificationStepper || !disableShortlist) && (
                <Flex
                  direction="column"
                  align="center"
                  gap="sm"
                  justify="flex-start"
                  style={{ flexShrink: 0 }}
                  miw={0}
                >
                  {showProfileVerificationStepper && (
                    <Box style={{ width: "100%", minWidth: 0 }}>
                      <ProfileVerificationStepper status={company.status} compact />
                    </Box>
                  )}
                  {!disableShortlist && (
                    <Button
                      variant="filled"
                      size="md"
                      p="xs"
                      onClick={handleShortlistClick}
                      styles={BOOKMARK_HEADER_BTN_STYLES}
                      style={{ flexShrink: 0 }}
                    >
                      <IconBookmark size={20} color="white" fill={isFavorited ? "currentColor" : "none"} />
                    </Button>
                  )}
                </Flex>
              )}
            </Flex>
          </Stack>

          {/* Buttons/Chips Section - Full width below company info */}
          {collaborationStatus &&
            collaborationStatus.status !== "PAYMENT_EXPIRED" &&
            collaborationStatus.status !== "BOTH_PAID" &&
            collaborationStatus.status !== "RECEIVER_DECLINED" &&
            (() => {
              // Determine if current user is requester or receiver
              const userCompanyId = userDetails?.company?.id;
              const isReceiver = userCompanyId === collaborationStatus.receiverCompanyId;
              const isRequested = collaborationStatus.status === "REQUESTED";

              // If incoming request (user is receiver and status is REQUESTED), show Accept/Decline buttons
              if (isRequested && isReceiver) {
                return (
                  <Flex direction="column" gap="xs" w="100%">
                    <Flex gap="xs" w="100%">
                      <Button
                        size="xs"
                        color="primary"
                        leftSection={<IconCheck size={16} />}
                        onClick={() => setAcceptModalOpened(true)}
                        style={{ flex: 1 }}
                      >
                        Accept
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        leftSection={<IconX size={16} />}
                        onClick={handleDecline}
                        loading={declineLoading}
                        style={{ flex: 1 }}
                      >
                        Decline
                      </Button>
                    </Flex>
                    {canIncomingReceiverDownloadAnonymizedPdf && (
                      <Button
                        variant="outline"
                        color="primary"
                        size="xs"
                        fullWidth
                        leftSection={<IconDownload size={16} />}
                        loading={anonymizedPdfLoading}
                        onClick={handleDownloadAnonymizedProfilePdf}
                      >
                        Download Profile PDF
                      </Button>
                    )}
                  </Flex>
                );
              }

              // Otherwise show the status chip
              const chipConfig = userCompanyId
                ? getCollaborationStatusConfig(
                  collaborationStatus.status,
                  userCompanyId === collaborationStatus.requesterCompanyId,
                  collaborationStatus.requesterPaid,
                  collaborationStatus.receiverPaid
                )
                : getCollaborationStatusConfig(collaborationStatus.status);

              return (
                <Box
                  px={16}
                  py={7}
                  style={{
                    borderRadius: "var(--mantine-radius-md)",
                    backgroundColor: chipConfig.bg,
                    color: chipConfig.text,
                    fontSize: "13px",
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                    width: "fit-content",
                  }}
                >
                  {chipConfig.label}
                </Box>
              );
            })()}
        </Paper>

        {/* Desktop: Company Card */}
        <Paper
          p="xl"
          radius="lg"
          mb="lg"
          style={{
            border: "1px solid #04305c",
            backgroundColor: "#ebf0ff",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          }}
          visibleFrom="sm"
        >
          <Flex gap="xl" align="flex-start" justify="space-between">
            <Flex gap="md" align="flex-start">
              <Flex
                w={80}
                h={80}
                style={{
                  borderRadius: 8,
                  border: "1px solid #04305c",
                  backgroundColor: "#F9FAFB",
                  flexShrink: 0,
                  overflow: "hidden",
                }}
                align="center"
                justify="center"
              >
                <IconBuilding size={40} color="#6B7280" />
              </Flex>

              <Flex direction="column">
                <Flex align="center" gap="sm" mb="xs" wrap="wrap">
                  <Title order={2} fw={400} fz={22}>
                    {company.gtid}
                  </Title>
                  {company.status === "VERIFIED" && (
                    <IconRosetteDiscountCheckFilled size={20} color="var(--mantine-color-primary-6)" />
                  )}
                </Flex>
                <Flex gap="md" mb="xs" wrap="wrap">
                  <Flex align="center" gap="xs">
                    {headquartersFlagEmoji ? (
                      <Text
                        component="span"
                        fz={20}
                        lh={1}
                        role="img"
                        aria-label={`${company.headquartersCountry ?? "Country"} flag`}
                      >
                        {headquartersFlagEmoji}
                      </Text>
                    ) : null}
                    <Text size="l" c="slate.7">
                      {company.headquartersCountry || "N/A"}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="xs">
                    <IconPhone size={20} color="#6B7280" />
                    <Text size="l" c="slate.7">
                      {company.phone || "XXXX XXXXX"}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="xs">
                    <IconMail size={20} color="#6B7280" />
                    <Text size="l" c="slate.7">
                      XXX@mail.com
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>

            <Flex direction="column" align="flex-end" gap="sm" style={{ flexShrink: 0 }}>
              {showProfileVerificationStepper && (
                <ProfileVerificationStepper status={company.status} compact />
              )}
              {!hideInterestCTA && (
                <Flex align="center" gap="sm">
                  {collaborationStatus &&
                    collaborationStatus.status !== "PAYMENT_EXPIRED" &&
                    collaborationStatus.status !== "BOTH_PAID" &&
                    collaborationStatus.status !== "RECEIVER_DECLINED" &&
                    (() => {
                      const userCompanyId = userDetails?.company?.id;
                      const isReceiver = userCompanyId === collaborationStatus.receiverCompanyId;
                      const isRequested = collaborationStatus.status === "REQUESTED";

                      if (isRequested && isReceiver) {
                        return (
                          <Flex gap="sm" align="center" wrap="wrap" justify="flex-end">
                            <Button
                              color="primary"
                              leftSection={<IconCheck size={20} />}
                              onClick={() => setAcceptModalOpened(true)}
                            >
                              Accept
                            </Button>
                            <Button
                              variant="outline"
                              color="gray"
                              leftSection={<IconX size={20} />}
                              onClick={handleDecline}
                              loading={declineLoading}
                            >
                              Decline
                            </Button>
                            {canIncomingReceiverDownloadAnonymizedPdf && (
                              <Button
                                variant="outline"
                                color="primary"
                                size="md"
                                leftSection={<IconDownload size={18} />}
                                loading={anonymizedPdfLoading}
                                onClick={handleDownloadAnonymizedProfilePdf}
                              >
                                Download Profile PDF
                              </Button>
                            )}
                          </Flex>
                        );
                      }

                      const chipConfig = userCompanyId
                        ? getCollaborationStatusConfig(
                          collaborationStatus.status,
                          userCompanyId === collaborationStatus.requesterCompanyId,
                          collaborationStatus.requesterPaid,
                          collaborationStatus.receiverPaid
                        )
                        : getCollaborationStatusConfig(collaborationStatus.status);

                      return (
                        <Box
                          px={16}
                          py={7}
                          style={{
                            borderRadius: "var(--mantine-radius-md)",
                            backgroundColor: chipConfig.bg,
                            color: chipConfig.text,
                            fontSize: "13px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chipConfig.label}
                        </Box>
                      );
                    })()}
                  <Button
                    variant="filled"
                    size="md"
                    p="xs"
                    onClick={handleShortlistClick}
                    styles={BOOKMARK_HEADER_BTN_STYLES}
                  >
                    <IconBookmark size={24} color="white" fill={isFavorited ? "currentColor" : "none"} />
                  </Button>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Paper>

        {/* Interest CTA Box */}
        {!hideInterestCTA && !isLoggedIn && (
          <Paper
            p={{ base: "md", sm: "xl" }}
            radius="lg"
            mb="lg"
            bg="primary.0"
            style={{ border: "1px solid var(--mantine-color-primary-2)" }}
          >
            <Flex direction="column" align="center" gap="md" py="md">
              <Flex direction="column" align="center" gap={4}>
                <Text size="lg" fw={600} c="primary.9" ta="center">
                  Interested in connecting with this company?
                </Text>
                <Text size="sm" c="primary.7" ta="center" maw={600}>
                  Sign up to express interest, view full contact details, and unlock collaboration features
                </Text>
              </Flex>

              <Flex
                gap="md"
                justify="center"
                direction={{ base: "column", xs: "row" }}
                w={{ base: "100%", xs: "auto" }}
              >
                <Button size="md" onClick={handleSignUpClick} color="primary" variant="filled">
                  Sign Up Free
                </Button>
                <Button variant="outline" size="md" onClick={handleBrowseCompaniesClick} bg="white">
                  Browse More Companies
                </Button>
              </Flex>
            </Flex>
          </Paper>
        )}

        {/* Payment deadline warning — last 1 minute of the 3-minute window (demo timings) */}
        {!hideInfoBanner &&
          collaborationStatus?.paymentDeadline &&
          displayMs != null &&
          displayMs > 0 &&
          isPaymentUiWarningUrgent(displayMs) && (
            <Box mb="md">
              <InfoBanner
                icon={IconExclamationCircle}
                iconColor={STATUS_COLORS.WARNING.text}
                title={`Payment window expires in ${formatPaymentHoursLabel(displayMs)}`}
                subtitle="Both parties must complete payment within 96 hours of the first payment."
                bgColor={STATUS_COLORS.WARNING.bg}
                borderColor={STATUS_COLORS.WARNING.border}
                titleColor={STATUS_COLORS.WARNING.text}
                subtitleColor={STATUS_COLORS.WARNING.text}
              />
            </Box>
          )}

        {/* Payment Done InfoBanner - Shows when BOTH_PAID */}
        {!hideInfoBanner && collaborationStatus?.status === "BOTH_PAID" && (
          <Box mb="md">
            <Paper
              p="md"
              radius="lg"
              bg={STATUS_COLORS.SUCCESS.bg}
              style={{ border: `1px solid ${STATUS_COLORS.SUCCESS.border}` }}
            >
              <Flex justify="space-between" align="center" gap="md" wrap="wrap">
                <Box style={{ flex: 1 }}>
                  <Text size="sm" fw={600} c={STATUS_COLORS.SUCCESS.text} mb={4}>
                    Payment Done
                  </Text>
                  <Text size="xs">
                    {bothSignedNda
                      ? "Both parties have completed payment and signed the NDA."
                      : "Both parties have completed payment. Please sign the NDA to access the workspace."}
                  </Text>
                </Box>
                {bothSignedNda && (
                  <Button
                    size="sm"
                    color={STATUS_COLORS.SUCCESS.text}
                    onClick={() => navigate(`/collaborations/${collaborationStatus.id}`)}
                  >
                    Open Workspace
                  </Button>
                )}
              </Flex>
            </Paper>
          </Box>
        )}

        {/* Payment Done InfoBanner - Shows when REQUESTER_PAID_ONLY and user is requester */}
        {!hideInfoBanner &&
          collaborationStatus?.status === "REQUESTER_PAID_ONLY" &&
          (() => {
            const userCompanyId = userDetails?.company?.id;
            const isRequester = collaborationStatus.requesterCompanyId === userCompanyId;

            // Only show this banner if the viewer is the requester who paid
            if (isRequester) {
              return (
                <Box mb="md">
                  <InfoBanner
                    title="Payment Done"
                    subtitle="You've completed payment. Waiting for the collaborator to finish theirs."
                    bgColor={STATUS_COLORS.SUCCESS.bg}
                    borderColor={STATUS_COLORS.SUCCESS.border}
                    titleColor={STATUS_COLORS.SUCCESS.text}
                    subtitleColor={STATUS_COLORS.SUCCESS.text}
                  />
                </Box>
              );
            }
            return null;
          })()}

        {/* Payment Done InfoBanner - Shows when RECEIVER_PAID_ONLY and user is receiver */}
        {!hideInfoBanner &&
          collaborationStatus?.status === "RECEIVER_PAID_ONLY" &&
          (() => {
            const userCompanyId = userDetails?.company?.id;
            const isReceiver = collaborationStatus.receiverCompanyId === userCompanyId;

            // Only show this banner if the viewer is the receiver who paid
            if (isReceiver) {
              return (
                <Box mb="md">
                  <InfoBanner
                    title="Payment Done"
                    subtitle="You've completed payment. Waiting for the collaborator to finish theirs."
                    bgColor={STATUS_COLORS.SUCCESS.bg}
                    borderColor={STATUS_COLORS.SUCCESS.border}
                    titleColor={STATUS_COLORS.SUCCESS.text}
                    subtitleColor={STATUS_COLORS.SUCCESS.text}
                  />
                </Box>
              );
            }
            return null;
          })()}

        {/* Ready to Collaborate Banner - Shows when BOTH_ACCEPTED, or when user hasn't paid yet */}
        {!hideInterestCTA &&
          isLoggedIn &&
          collaborationStatus &&
          (() => {
            const userCompanyId = userDetails?.company?.id;
            if (!userCompanyId) return false;

            const isRequester = collaborationStatus.requesterCompanyId === userCompanyId;
            const userPaid = isRequester ? collaborationStatus.requesterPaid : collaborationStatus.receiverPaid;

            // Show banner if:
            // 1. Status is BOTH_ACCEPTED and user hasn't paid
            // 2. Status is REQUESTER_PAID_ONLY and user is receiver (hasn't paid)
            // 3. Status is RECEIVER_PAID_ONLY and user is requester (hasn't paid)
            return (
              (collaborationStatus.status === "BOTH_ACCEPTED" && !userPaid) ||
              (collaborationStatus.status === "REQUESTER_PAID_ONLY" && !isRequester) ||
              (collaborationStatus.status === "RECEIVER_PAID_ONLY" && isRequester)
            );
          })() && (
            <Paper p="xl" radius="lg" mb="lg" bg="white" style={{ border: "1px solid #E5E7EB" }}>
              <Flex justify="space-between" align="center" gap="xl" wrap="wrap">
                <Box style={{ flex: 1 }}>
                  {collaborationStatus?.status === "RECEIVER_PAID_ONLY" ||
                    collaborationStatus?.status === "REQUESTER_PAID_ONLY" ? (
                    <Box
                      px={16}
                      py={7}
                      mb={4}
                      style={{
                        borderRadius: "var(--mantine-radius-md)",
                        backgroundColor: "var(--mantine-color-red-0)",
                        color: "var(--mantine-color-red-7)",
                        fontSize: "13px",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        width: "fit-content",
                      }}
                    >
                      Payment Pending
                    </Box>
                  ) : (
                    <Text size="lg" fw={600} mb={4} c="gray.9">
                      Ready to Collaborate?
                    </Text>
                  )}
                  <Text size="sm" c="gray.6">
                    {collaborationStatus?.status === "RECEIVER_PAID_ONLY" ||
                      collaborationStatus?.status === "REQUESTER_PAID_ONLY"
                      ? "The collaborator has completed payment. Complete yours to proceed."
                      : "To collaborate and to unlock full contact details of this company, make payment."}
                  </Text>
                </Box>
                <Button
                  size="md"
                  color="primary"
                  onClick={() => {
                    navigate(`/requests/match-success/${collaborationStatus.id}`, {
                      state: {
                        request: {
                          ...collaborationStatus,
                          receiver: company,
                          requester: company,
                        },
                      },
                    });
                  }}
                >
                  Make Payment
                </Button>
              </Flex>
            </Paper>
          )}

        {/* Ready to Connect Banner */}
        {!hideInterestCTA && isLoggedIn && !collaborationStatus && (
          <Paper
            p="md"
            radius="lg"
            mb="lg"
            bg="var(--mantine-color-primary-0)"
            style={{ border: "1px solid var(--mantine-color-primary-2)" }}
          >
            <Flex justify="space-between" align="center" gap="xl" wrap="wrap">
              <Box style={{ flex: 1 }}>
                <Text size="lg" fw={400} mb={4} c="gray.9">
                  Ready to Connect?
                </Text>
                <Text size="sm" c="gray.6" mb="md">
                  Express your interest to unlock full contact details and start a direct conversation with this
                  company.
                </Text>
                {/* <Flex gap="xl" wrap="wrap">
                  <Flex align="center" gap="xs">
                    <IconEye size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      View full contact information
                    </Text>
                  </Flex>
                  <Flex align="center" gap="xs">
                    <IconCreditCard size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      One-time fee of $199
                    </Text>
                  </Flex>
                </Flex> */}
              </Box>
              <Flex gap="sm" align="center" wrap="wrap" justify="flex-end">
                <Button
                  variant="outline"
                  color="primary"
                  size="md"
                  leftSection={<IconDownload size={18} />}
                  loading={anonymizedPdfLoading}
                  onClick={handleDownloadAnonymizedProfilePdf}
                >
                  Download Profile PDF
                </Button>
                <Button size="md" color="primary" onClick={handleExpressInterestClick}>
                  Express Interest
                </Button>
              </Flex>
            </Flex>
          </Paper>
        )}

        {/* Protected Info Alert */}
        {!hideInfoBanner && (
          <Box mb="md">
            <InfoBanner
              icon={IconLock}
              iconSize={24}
              title="Contact Information Protected"
              subtitle="Direct contact details (email, phone) are available after mutual interest is confirmed and payment is processed."
            />
          </Box>
        )}

        {/* Tabs Section */}
        <Tabs value={activeTab} onChange={setActiveTab} styles={profileNavTabsRootStyles}>
          <Box w="100%" style={NAV_TABS_FULL_WIDTH_LINE_STYLE}>
            <Tabs.List styles={profileNavTabsListStyles}>
              <Tabs.Tab
                value="profile"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  backgroundColor: activeTab === "profile" ? "#04305c" : "#ebf0ff",
                  color: activeTab === "profile" ? "white" : "var(--mantine-color-slate-6)",
                  borderRadius: navyProfileNavTabBorderRadius(isProfileNavMobile, "first"),
                  border: activeTab === "profile" ? "1px solid #04305c" : "1px solid #E5E7EB",
                  borderBottom: "none",
                  fontWeight: activeTab === "profile" ? 500 : 400,
                  cursor: "pointer",
                  minHeight: 44,
                }}
                rightSection={
                  <Box
                    w={28}
                    h={28}
                    miw={28}
                    mih={28}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  />
                }
              >
                Company Profile
              </Tabs.Tab>
              <Tabs.Tab
                value="images"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  backgroundColor: activeTab === "images" ? "#04305c" : "#ebf0ff",
                  color: activeTab === "images" ? "white" : "var(--mantine-color-slate-6)",
                  borderRadius: navyProfileNavTabBorderRadius(isProfileNavMobile, "last"),
                  border: activeTab === "images" ? "1px solid #04305c" : "1px solid #E5E7EB",
                  borderBottom: "none",
                  fontWeight: activeTab === "images" ? 500 : 400,
                  cursor: "pointer",
                  minHeight: 44,
                }}
                rightSection={
                  <Box
                    w={28}
                    h={28}
                    miw={28}
                    mih={28}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  />
                }
              >
                Images and Documents
              </Tabs.Tab>
            </Tabs.List>
          </Box>

          <Tabs.Panel value="profile" pt="xl">
            <ProfileTab
              company={company}
              collaboration={collaborationStatus}
              onDownloadVerificationPdf={onDownloadVerificationPdf}
              verificationPdfDownloading={verificationPdfDownloading}
            />
          </Tabs.Panel>

          <Tabs.Panel value="images" pt="xl">
            <ImagesTabContent
              company={company}
              refreshCompany={async () => { }}
              isViewMode={true}
              anonymousView={true}
            />
            <Divider my="xl" size={2} color="#04305c" styles={{ root: { boxShadow: "none" } }} />
            <DocumentsTab company={company} />
          </Tabs.Panel>
        </Tabs>
      </Container>

      <ShortlistModal opened={modalOpened} onClose={() => setModalOpened(false)} companyName="Anonymous Company" />

      <ExpressInterestModal
        opened={expressInterestModalOpened}
        onClose={closeExpressInterestModal}
        onConfirm={handleConfirmExpressInterest}
        loading={requestLoading}
        userWhoAmI={(userDetails?.company?.whoAmI as string[]) || []}
        targetCollaborationIntent={(company?.collaborationIntent as string[]) || []}
      />

      <GenericModal
        disclosure={[removeModalOpened, { open: openRemoveModal, close: closeRemoveModal, toggle: () => { } }]}
        title="Remove Shortlist"
        subtitle="Are you sure you want to remove this company from your shortlist?"
        buttons={[
          {
            label: "Remove",
            onClick: handleRemoveConfirm,
            variant: "filled",
            color: "primary",
            loading: removeLoading,
          },
        ]}
      />

      <AcceptInterestModal
        opened={acceptModalOpened}
        onClose={() => setAcceptModalOpened(false)}
        onConfirm={handleAcceptConfirm}
        isLoading={acceptLoading}
      />

      <PaymentWindowExtendModal
        opened={modalOpen}
        onExtend={handleExtend}
        onDismiss={handleModalDismiss}
      />
    </Box>
  );
}
